import removeBackground from "@imgly/background-removal";
import { type Canvas, FabricImage } from "fabric";
import { IMAGE_LAYER_TYPE } from "../constants.ts";
import type {
  AssetLayer,
  CanvasSize,
  ImageLayer,
  ImageLayerProperties,
  ImageLayerSettings,
} from "../types.ts";
import { createLayer, getLayers } from "./layer.ts";
import { updateLayerStrokeStyle } from "./stroke.ts";

export const createImageLayer = ({
  fabricCanvas,
  contentUuid,
  name,
  positionIndex,
  options,
  canvasSize,
}: {
  fabricCanvas: Canvas;
  contentUuid: string;
  name?: string;
  positionIndex?: number;
  options: ImageLayerProperties;
  canvasSize: CanvasSize;
}) => {
  const imageLayer = new FabricImage(new Image(), {
    left: options.xPosition ?? canvasSize.width / 2,
    top: options.yPosition ?? canvasSize.height / 2,
    scaleX: options.scaleX ?? 1,
    scaleY: options.scaleY ?? 1,
    width: 100,
    height: 100,
    angle: options.rotation ?? 0,
    crossOrigin: "anonymous",
    originX: "center",
    originY: "center",
    stroke: options.stroke ?? "rgba(0, 0, 0, 1)",
    strokeWidth: options.strokeWidth ?? 0,
  }) as ImageLayer;
  imageLayer.contentUuid = contentUuid;
  imageLayer.displayScale = 1;
  updateLayerStrokeStyle(imageLayer, options.strokeStyle ?? "solid");
  const currentLayerCount = getLayers(fabricCanvas).length;
  // FIXME: We should add temporary image layer to the fabric canvas to make clearer the fact that we are currently loading the image.
  return createLayer({
    fabricCanvas,
    layer: imageLayer,
    name: name ?? `Image ${currentLayerCount + 1}`,
    positionIndex: positionIndex ?? currentLayerCount,
    layerType: IMAGE_LAYER_TYPE,
  });
};

export const buildImageLayer = async ({
  imageLayer,
  targetWidth,
  targetHeight,
  imageUrl,
}: {
  imageLayer: ImageLayer | AssetLayer;
  targetWidth: number;
  targetHeight: number;
  imageUrl?: string;
}) => {
  const canvas = imageLayer.canvas;
  if (!canvas) return;

  if (!imageUrl) {
    await imageLayer.setSrc("");
    canvas.renderAll();
    return;
  }

  // XXX: We don't use the width and height parameters here because we want to use the original image dimensions instead.
  // The reason behind is that we don't necessarily know the original image dimensions that can depend on the image proxy / GET content arguments.
  // The temporary image layer is created with a default 100x100 width and height, and we will scale it to the original image dimensions.
  await FabricImage.fromURL(imageUrl, {
    // Set crossOrigin to 'anonymous' to enable CORS-safe image loading. This allows the canvas
    // to read pixel data from images loaded from different origins without throwing security errors.
    // Without this, operations like getImageData(), toDataURL(), or toBlob() would throw:
    // "DOMException: Failed to execute 'getImageData' on 'CanvasRenderingContext2D': The canvas has been tainted by cross-origin data"
    // See: https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image
    crossOrigin: "anonymous",
  });
  // We store the original scale to be able to restore it after the image is loaded, indeed the `setSrc` method will reset the scale and dimensions of the image layer.
  const originalScaleX = imageLayer.scaleX / imageLayer.displayScale;
  const originalScaleY = imageLayer.scaleY / imageLayer.displayScale;

  await imageLayer.setSrc(imageUrl, {
    crossOrigin: "anonymous",
  });

  const srcImageWidth = imageLayer.width;
  const srcImageHeight = imageLayer.height;

  const newDisplayScaleX = targetWidth / srcImageWidth;
  const newDisplayScaleY = targetHeight / srcImageHeight;

  // XXX: This is an arbitrary choice that works well when the asset.content is a children of the overlay, we should use max if we are in the other order.
  const newDisplayScale = Math.min(newDisplayScaleX, newDisplayScaleY);

  imageLayer.set({
    scaleX: newDisplayScale * originalScaleX,
    scaleY: newDisplayScale * originalScaleY,
    displayScale: newDisplayScale,
  });
  imageLayer.setCoords();

  canvas.renderAll();
};

export const duplicateImageLayer = (
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
  imageLayer: ImageLayer,
) =>
  createImageLayer({
    fabricCanvas,
    canvasSize,
    contentUuid: imageLayer.contentUuid,
    options: {
      xPosition: imageLayer.left,
      yPosition: imageLayer.top,
      rotation: imageLayer.angle,
      stroke: imageLayer.stroke,
      strokeWidth: imageLayer.strokeWidth,
      strokeStyle: imageLayer.strokeStyle,
      scaleX: imageLayer.scaleX,
      scaleY: imageLayer.scaleY,
    },
  });

export const updateImageLayer = (
  imageLayer: ImageLayer,
  options: Partial<Required<ImageLayerSettings>>,
) => {
  imageLayer.canvas?.fire("layer:image:changed", {
    layer: imageLayer,
    changes: options,
  });
  imageLayer.set({
    angle: options.rotation ?? imageLayer.angle,
    stroke: options.stroke ?? imageLayer.stroke,
    strokeWidth: options.strokeWidth ?? imageLayer.strokeWidth,
  });
  if (options.strokeStyle !== undefined) {
    updateLayerStrokeStyle(imageLayer, options.strokeStyle);
  }
  imageLayer.setCoords();
};
export const removeImageLayerBackground = async (
  fabricCanvas: Canvas,
  imageLayer: ImageLayer,
  uploadImage: ({
    image,
    onMutateSuccess,
  }: {
    image: Blob;
    onMutateSuccess?: (uuid: string) => void;
  }) => void,
): Promise<null> => {
  const imageElement = imageLayer.getElement() as HTMLImageElement;

  if (!imageElement.src) {
    throw new Error(`No valid image found in layer ${imageLayer.name}`);
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Failed to get canvas context");

  canvas.width = imageElement.naturalWidth;
  canvas.height = imageElement.naturalHeight;

  ctx.drawImage(imageElement, 0, 0);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blobToConvert) => {
      if (blobToConvert) {
        resolve(blobToConvert);
      } else {
        reject(new Error("Failed to convert canvas to blob"));
      }
    }, "image/png");
  });

  const processedBlob = await removeBackground(blob);
  const oldContentUuid = imageLayer.contentUuid;

  uploadImage({
    image: processedBlob,
    onMutateSuccess: (uuid) => {
      imageLayer.contentUuid = uuid;
      fabricCanvas.fire("layer:image:contentUuid:changed", {
        layer: imageLayer,
        oldContentUuid,
        newContentUuid: uuid,
      });
    },
  });

  return null;
};
